/**
 * PostHog Integration — Client Observability (PLAT-9155, PLAT-9166)
 *
 * Full PostHog client wrapper: session replay, autocapture, pageviews,
 * heatmaps, web analytics, and AI event tracking. Feature-flagged via
 * LaunchDarkly, non-blocking, and no-op when API key is missing.
 *
 * Mirrors Datadog integration patterns for consistent user tracking,
 * context enrichment, and event routing across both systems.
 *
 * Backend companion: PLAT-9033 (PostHog AI Observability — Services Backend)
 * User identity is linked via `user_email` as the PostHog `distinct_id`.
 */

import posthog from 'posthog-js';

interface PostHogInitMetadata {
  service: string;
  env: string;
  version: string | undefined;
}

let initialized = false;
let aiObservabilityEnabled = false;

export const PostHogIntegration = {
  init(apiKey: string, metadata?: PostHogInitMetadata): void {
    if (!apiKey || initialized) return;
    posthog.init(apiKey, {
      api_host: '/phfwd',
      ui_host: 'https://us.posthog.com',
      autocapture: true,
      capture_pageview: false,
      capture_pageleave: false,
      person_profiles: 'identified_only',
      capture_performance: true,
      session_recording: {
        maskAllInputs: true,
      },
    });
    // WARNING: `source` is reserved as a PostHog super-property here
    // (always set to 'frontend'). Do NOT pass `source` as a custom
    // event property — it will be silently overwritten. Use
    // `action_source` for per-event surface/origin tagging instead.
    // See PLAT-9680.
    posthog.register({
      source: 'frontend',
      environment: metadata?.env ?? process.env.REACT_APP_DATO_ENV ?? 'dev',
      service: metadata?.service ?? 'arbor-video',
      ...(metadata?.version ? { version: metadata.version } : {}),
    });
    initialized = true;

    window.addEventListener('beforeunload', () => {
      posthog.capture('$pageleave');
      PostHogIntegration.shutdown();
    });
  },

  setAIObservabilityEnabled(value: boolean): void {
    aiObservabilityEnabled = value;
  },

  isAIObservabilityEnabled(): boolean {
    return initialized && aiObservabilityEnabled;
  },

  isInitialized(): boolean {
    return initialized;
  },

  identify(userId: string, traits: Record<string, unknown>): void {
    if (!initialized) return;
    posthog.identify(userId, traits);
  },

  group(
    groupType: string,
    groupKey: string,
    properties?: Record<string, unknown>,
  ): void {
    if (!initialized) return;
    posthog.group(groupType, groupKey, properties);
  },

  setContext(properties: Record<string, unknown>): void {
    if (!initialized) return;
    posthog.register(properties);
  },

  capture(event: string, properties: Record<string, unknown>): void {
    if (!initialized) return;
    try {
      // WARNING: `source` is reserved as a PostHog super-property here
      // (always set to 'frontend'). Do NOT pass `source` as a custom
      // event property — it will be silently overwritten. Use
      // `action_source` for per-event surface/origin tagging instead.
      // See PLAT-9680.
      posthog.capture(event, { ...properties, source: 'frontend' });
    } catch {
      // Non-blocking
    }
  },

  captureAIEvent(event: string, properties: Record<string, unknown>): void {
    if (!this.isAIObservabilityEnabled()) return;
    try {
      posthog.capture(event, { ...properties, source: 'frontend' });
    } catch {
      // Non-blocking — never delay AI operations
    }
  },

  captureException(
    error: Error | string,
    properties?: Record<string, unknown>,
  ): void {
    if (!initialized) return;
    try {
      const err = typeof error === 'string' ? new Error(error) : error;
      posthog.capture('$exception', {
        $exception_message: err.message,
        $exception_stack_trace_raw: err.stack,
        ...properties,
        source: 'frontend',
      });
    } catch {
      // Non-blocking
    }
  },

  shutdown(): void {
    if (!initialized) return;
    try {
      posthog.reset();
    } catch {
      // Non-blocking
    }
    initialized = false;
    aiObservabilityEnabled = false;
  },
};
